/**
 * Presence Utility Functions
 * Helper utilities extracted from the working test app for consistency
 * These match the patterns used in frontend/web_sockets_test/PresenceHub/react-app
 */
import { PresenceStatus } from './types';
/**
 * Get human-readable status name from enum
 * @param status - Presence status enum value
 * @returns Status name string
 */
export function getStatusName(status) {
    return status.toString();
}
/**
 * Get status color for UI (hex color)
 * Matches the test app's color scheme
 * @param status - Presence status enum value
 * @returns Hex color string
 */
export function getStatusColor(status) {
    switch (status) {
        case PresenceStatus.Offline:
            return '#9e9e9e'; // Gray
        case PresenceStatus.Online:
            return '#4CAF50'; // Green
        case PresenceStatus.Away:
            return '#FF9800'; // Orange
        case PresenceStatus.Busy:
            return '#f44336'; // Red
        default:
            return '#9e9e9e';
    }
}
/**
 * Generate user initials from name
 * Falls back to userId if name is not available
 * @param userName - User's display name (can be null/undefined)
 * @param userId - User's ID (fallback)
 * @returns Two-letter initials in uppercase
 */
export function getUserInitials(userName, userId) {
    if (userName && userName.trim()) {
        const parts = userName.trim().split(' ');
        if (parts.length >= 2) {
            // First and last name: "John Doe" -> "JD"
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        // Single name: "John" -> "JO"
        return userName.substring(0, 2).toUpperCase();
    }
    // Fallback to userId: "user123" -> "US"
    return userId.substring(0, 2).toUpperCase();
}
/**
 * Generate deterministic avatar color from userId
 * Uses hash function to consistently assign one of 10 colors
 * @param userId - User's ID
 * @returns Hex color string
 */
export function getAvatarColor(userId) {
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
        hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    }
    // 10 material design colors
    const colors = [
        '#E91E63', // Pink
        '#9C27B0', // Purple
        '#673AB7', // Deep Purple
        '#3F51B5', // Indigo
        '#2196F3', // Blue
        '#00BCD4', // Cyan
        '#009688', // Teal
        '#4CAF50', // Green
        '#FF9800', // Orange
        '#FF5722', // Deep Orange
    ];
    return colors[Math.abs(hash) % colors.length];
}
/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param lastSeenDate - Last seen date (ISO-8601 string or Date object)
 * @returns Human-readable relative time string
 */
export function getRelativeTime(lastSeenDate) {
    const now = Date.now();
    const lastSeen = new Date(lastSeenDate).getTime();
    const diffMs = now - lastSeen;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1)
        return 'Active now';
    if (diffMins === 1)
        return '1 minute ago';
    if (diffMins < 60)
        return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1)
        return '1 hour ago';
    if (diffHours < 24)
        return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1)
        return '1 day ago';
    return `${diffDays} days ago`;
}
/**
 * Extract userId from JWT token
 * Supports multiple claim formats used by different auth systems
 * @param token - JWT token string
 * @returns User ID or null if extraction fails
 */
export function extractUserIdFromToken(token) {
    try {
        // Split token and decode payload (middle part)
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Try different claim names (in order of preference)
        return (payload.sub ||
            payload.nameid ||
            payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
            null);
    }
    catch (error) {
        console.error('[extractUserIdFromToken] Failed to extract userId:', error);
        return null;
    }
}
/**
 * Extract admin role from JWT token
 * Checks if the token contains "Admin" role
 * @param token - JWT token string
 * @returns True if user has Admin role, false otherwise
 */
export function extractAdminRoleFromToken(token) {
    try {
        // Split token and decode payload (middle part)
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Try different role claim names
        const roles = payload.role || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        // Roles can be a string or array
        if (Array.isArray(roles)) {
            return roles.includes('Admin');
        }
        return roles === 'Admin';
    }
    catch (error) {
        console.error('[extractAdminRoleFromToken] Failed to extract admin role:', error);
        return false;
    }
}
/**
 * Format timestamp to locale string
 * @param timestamp - ISO-8601 timestamp string
 * @returns Formatted date/time string
 */
export function formatTimestamp(timestamp) {
    try {
        return new Date(timestamp).toLocaleString();
    }
    catch (error) {
        return timestamp;
    }
}
/**
 * Status preset configurations
 * Matches the test app's quick status buttons
 */
export const STATUS_PRESETS = {
    available: {
        status: PresenceStatus.Online,
        message: '',
    },
    onBreak: {
        status: PresenceStatus.Away,
        message: 'On break',
    },
    inMeeting: {
        status: PresenceStatus.Busy,
        message: 'In a meeting',
    },
    doNotDisturb: {
        status: PresenceStatus.Busy,
        message: 'Do not disturb',
    },
    appearOffline: {
        status: PresenceStatus.Offline,
        message: 'Appear offline',
    },
};
//# sourceMappingURL=utils.js.map